import { useState, useCallback } from 'react';
import {
  makeStyles,
  tokens,
  Body1,
  Body2,
  Caption1,
  Badge,
  Button,
  Avatar,
  Drawer,
  DrawerHeader,
  DrawerHeaderTitle,
  DrawerBody,
  Spinner,
  MessageBar,
  MessageBarBody,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from '@fluentui/react-components';
import { PageTitle } from '@/components/PageTitle';
import {
  ArrowSync24Regular,
  Dismiss24Regular,
  Briefcase24Regular,
  Apps24Regular,
} from '@fluentui/react-icons';
import { DataTable, type Column } from '@/components/DataTable';
import { useSharePointList } from '@/hooks/useSharePointList';
import { useAppContext } from '@/store/appContext';
import type {
  Employee,
  Allocation,
  AppAssignment,
  Project,
  Application,
  SPListItem,
} from '@/models/types';
import { getInitials, formatDate, formatPercent } from '@/utils/formatters';
import { canSyncData } from '@/utils/permissions';
import { fetchEntraUsers, syncUsersToSharePoint, type SyncResult } from '@/graph/userService';

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: '20px' },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap' as const,
    gap: '12px',
  },
  memberRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  memberInfo: { minWidth: 0 },
  drawerSection: {
    marginBottom: '20px',
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '12px',
    fontWeight: 600,
  },
  detailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 0',
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
  },
});

export function Members() {
  const styles = useStyles();
  const { graphClient, currentUser, listIds } = useAppContext();
  const [selectedMember, setSelectedMember] = useState<SPListItem<Employee> | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [showSyncDialog, setShowSyncDialog] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');

  const { items: employees, loading, error: loadError, refresh } = useSharePointList<Employee>(
    'OC_Employees',
    { autoFetch: !!listIds },
  );
  const { items: allocations } = useSharePointList<Allocation>(
    'OC_Allocations',
    { autoFetch: !!listIds },
  );
  const { items: assignments } = useSharePointList<AppAssignment>(
    'OC_AppAssignments',
    { autoFetch: !!listIds },
  );
  const { items: projects } = useSharePointList<Project>('OC_Projects', {
    autoFetch: !!listIds,
  });
  const { items: apps } = useSharePointList<Application>('OC_Applications', {
    autoFetch: !!listIds,
  });

  const filteredEmployees =
    statusFilter === 'all'
      ? employees
      : employees.filter((e) => e.fields.Status === statusFilter);

  const handleSync = useCallback(async () => {
    if (!graphClient) return;
    setSyncing(true);
    setSyncError(null);
    setSyncResult(null);

    try {
      const entraUsers = await fetchEntraUsers(graphClient);
      const result = await syncUsersToSharePoint(graphClient, entraUsers);
      setSyncResult(result);
      setShowSyncDialog(true);
      await refresh();
    } catch (err) {
      setSyncError(err instanceof Error ? err.message : 'Sync failed');
    } finally {
      setSyncing(false);
    }
  }, [graphClient, refresh]);

  const getMemberAllocations = (empId: string) =>
    allocations.filter((a) => a.fields.EmployeeId === Number(empId));

  const getMemberAssignments = (empId: string) =>
    assignments.filter(
      (a) =>
        a.fields.EmployeeId === Number(empId) && a.fields.Status === 'Active',
    );

  const getProjectName = (projectId: number) =>
    projects.find((p) => Number(p.id) === projectId)?.fields.Title ?? 'Unknown';

  const getAppName = (appId: number) =>
    apps.find((a) => Number(a.id) === appId)?.fields.Title ?? 'Unknown';

  const columns: Column<SPListItem<Employee>>[] = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      getValue: (item) => item.fields.Title,
      render: (item) => (
        <div className={styles.memberRow}>
          <Avatar
            name={item.fields.Title}
            initials={getInitials(item.fields.Title)}
            size={32}
            color="brand"
          />
          <div className={styles.memberInfo}>
            <Body2 style={{ fontWeight: 500 }}>{item.fields.Title}</Body2>
            <Caption1>{item.fields.Email}</Caption1>
          </div>
        </div>
      ),
    },
    {
      key: 'department',
      label: 'Department',
      sortable: true,
      getValue: (item) => item.fields.Department,
    },
    {
      key: 'jobTitle',
      label: 'Job Title',
      getValue: (item) => item.fields.JobTitle,
    },
    {
      key: 'status',
      label: 'Status',
      render: (item) => (
        <Badge
          appearance="filled"
          color={item.fields.Status === 'Active' ? 'success' : 'danger'}
          size="small"
        >
          {item.fields.Status}
        </Badge>
      ),
      getValue: (item) => item.fields.Status,
      sortable: true,
    },
    {
      key: 'joined',
      label: 'Joined',
      getValue: (item) => item.fields.JoinDate,
      render: (item) => <Caption1>{formatDate(item.fields.JoinDate)}</Caption1>,
      sortable: true,
    },
  ];

  const activeCount = employees.filter(
    (e) => e.fields.Status === 'Active',
  ).length;
  const inactiveCount = employees.filter(
    (e) => e.fields.Status === 'Inactive',
  ).length;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <PageTitle>Members</PageTitle>
      </div>

      {syncError && (
        <MessageBar intent="error">
          <MessageBarBody>{syncError}</MessageBarBody>
        </MessageBar>
      )}
      {loadError && (
        <MessageBar intent="error">
          <MessageBarBody>Failed to load members: {loadError}</MessageBarBody>
        </MessageBar>
      )}

      <DataTable
        columns={columns}
        items={filteredEmployees}
        loading={loading}
        searchPlaceholder="Search members..."
        searchFields={(item) =>
          `${item.fields.Title} ${item.fields.Email} ${item.fields.Department} ${item.fields.JobTitle}`
        }
        getRowKey={(item) => item.id}
        onRowClick={setSelectedMember}
        emptyMessage="No members found. Sync from Entra ID to get started."
        renderMobile={(item) => ({
          leading: (
            <Avatar
              name={item.fields.Title}
              initials={getInitials(item.fields.Title)}
              size={36}
              color="colorful"
            />
          ),
          title: item.fields.Title,
          subtitle: item.fields.JobTitle || item.fields.Email,
          meta: item.fields.Department,
          trailing:
            item.fields.Status === 'Active' ? null : (
              <Badge appearance="tint" color="informative" size="small">
                Inactive
              </Badge>
            ),
        })}
        groupBy={(item) => item.fields.Department || 'No department'}
        filterTabs={[
          { key: 'all', label: 'All', count: employees.length },
          { key: 'Active', label: 'Active', count: activeCount },
          { key: 'Inactive', label: 'Inactive', count: inactiveCount },
        ]}
        activeFilter={statusFilter}
        onFilterChange={setStatusFilter}
        actions={
          currentUser && canSyncData(currentUser.role) ? (
            <Button
              appearance="primary"
              icon={syncing ? <Spinner size="tiny" /> : <ArrowSync24Regular />}
              disabled={syncing}
              onClick={handleSync}
            >
              {syncing ? 'Syncing...' : 'Sync from Entra ID'}
            </Button>
          ) : undefined
        }
      />

      {/* Sync Result Dialog */}
      <Dialog open={showSyncDialog} onOpenChange={() => setShowSyncDialog(false)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Sync Complete</DialogTitle>
            <DialogContent>
              {syncResult && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <Body1>Created: {syncResult.created} new members</Body1>
                  <Body1>Updated: {syncResult.updated} members</Body1>
                  <Body1>Deactivated: {syncResult.deactivated} members</Body1>
                </div>
              )}
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="primary">OK</Button>
              </DialogTrigger>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Member Detail Drawer */}
      <Drawer
        type="overlay"
        position="end"
        open={!!selectedMember}
        onOpenChange={() => setSelectedMember(null)}
        size="medium"
      >
        {selectedMember && (
          <>
            <DrawerHeader>
              <DrawerHeaderTitle
                action={
                  <Button
                    appearance="subtle"
                    icon={<Dismiss24Regular />}
                    onClick={() => setSelectedMember(null)}
                  />
                }
              >
                {selectedMember.fields.Title}
              </DrawerHeaderTitle>
            </DrawerHeader>
            <DrawerBody>
              <div style={{ padding: '8px 0', display: 'flex', flexDirection: 'column', gap: 4 }}>
                <Caption1>{selectedMember.fields.Email}</Caption1>
                <Caption1>
                  {selectedMember.fields.Department} &middot;{' '}
                  {selectedMember.fields.JobTitle}
                </Caption1>
                <Badge
                  appearance="filled"
                  color={selectedMember.fields.Status === 'Active' ? 'success' : 'danger'}
                  size="small"
                  style={{ alignSelf: 'flex-start', marginTop: 4 }}
                >
                  {selectedMember.fields.Status}
                </Badge>
              </div>

              <Divider style={{ margin: '16px 0' }} />

              {/* Allocations */}
              <div className={styles.drawerSection}>
                <div className={styles.sectionTitle}>
                  <Briefcase24Regular />
                  <Body1>Project Allocations</Body1>
                </div>
                {getMemberAllocations(selectedMember.id).length === 0 ? (
                  <Caption1>No project allocations</Caption1>
                ) : (
                  getMemberAllocations(selectedMember.id).map((alloc, idx) => (
                    <div key={idx} className={styles.detailRow}>
                      <Body1>{getProjectName(alloc.fields.ProjectId)}</Body1>
                      <Badge appearance="outline" size="small">
                        {alloc.fields.Role} &middot;{' '}
                        {formatPercent(alloc.fields.AllocationPercent)}
                      </Badge>
                    </div>
                  ))
                )}
              </div>

              {/* App Access */}
              <div className={styles.drawerSection}>
                <div className={styles.sectionTitle}>
                  <Apps24Regular />
                  <Body1>App Access</Body1>
                </div>
                {getMemberAssignments(selectedMember.id).length === 0 ? (
                  <Caption1>No apps assigned</Caption1>
                ) : (
                  getMemberAssignments(selectedMember.id).map((assign, idx) => (
                    <div key={idx} className={styles.detailRow}>
                      <Body1>{getAppName(assign.fields.AppId)}</Body1>
                      <Badge appearance="outline" color="success" size="small">
                        Active
                      </Badge>
                    </div>
                  ))
                )}
              </div>
            </DrawerBody>
          </>
        )}
      </Drawer>
    </div>
  );
}
